import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { chatterGuard } from './guards/chatter.guard';
import { coffeeFundraisingGuard } from './guards/coffee-fundraising.guard';
import { guestGuard } from './guards/guest.guard';
import { tripAgreementsGuard } from './guards/trip-agreements.guard';
import { dateOfBirthGateGuard, dateOfBirthGatePageGuard } from './guards/date-of-birth-gate.guard';
import { panelContextGuard } from './guards/panel-context.guard';
import { tripFinancesGuard } from './guards/trip-finances.guard';
import { moneyOutPaymentGuard } from './guards/money-out-payment.guard';
import { requestTravelQuoteGuard } from './guards/request-travel-quote.guard';
import { teamDocumentUploadGuard } from './guards/team-document-upload.guard';
import { tripDocumentsGuard } from './guards/trip-documents.guard';
import { paymentGuard } from './guards/payment.guard';
import { surveyGuard } from './guards/survey.guard';
import { dashboardRoute } from './guards/trip-switch-routes';

const requireAuth = [authGuard, dateOfBirthGateGuard];
const requireTripAccess = [...requireAuth, tripAgreementsGuard];

export const commonRoutes: Routes = [
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'forgot-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
  },
  {
    path: 'reset',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
  },
  {
    path: 'reset-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
  },
  {
    path: 'verify-account',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/verify-account/verify-account.component').then(m => m.VerifyAccountComponent),
  },
  {
    path: 'verify-account-question',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/verify-account-question/verify-account-question.component').then(
        m => m.VerifyAccountQuestionComponent,
      ),
  },
  {
    path: 'invalid-token',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/invalid-token/invalid-token.component').then(m => m.InvalidTokenComponent),
  },
  {
    path: 'invalid-verification-token',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/invalid-verification-token/invalid-verification-token.component').then(
        m => m.InvalidVerificationTokenComponent,
      ),
  },
  {
    path: 'trip-survey',
    loadComponent: () =>
      import('./pages/trip-survey-external/trip-survey-external.page').then(
        m => m.TripSurveyExternalPage,
      ),
  },
  {
    path: 'onboarding',
    canActivate: [authGuard, dateOfBirthGatePageGuard],
    loadComponent: () =>
      import('./pages/date-of-birth-gate/date-of-birth-gate.component').then(
        (m) => m.DateOfBirthGateComponent,
      ),
  },
  {
    path: 'trip-agreements',
    canActivate: requireAuth,
    loadComponent: () =>
      import('./pages/trip-agreements/trip-agreements.page').then(m => m.TripAgreementsPage),
  },
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    ...dashboardRoute,
    loadComponent: () => import('./pages/dashboard/dashboard.page').then(m => m.DashboardPage),
  },
  {
    path: 'forms',
    canActivate: requireTripAccess,
    loadComponent: () => import('./pages/forms/forms.page').then(m => m.FormsPage),
  },
  {
    path: 'trip-info',
    canActivate: requireTripAccess,
    loadComponent: () => import('./pages/trip-details/trip-info.page').then(m => m.TripInfoPage),
  },
  {
    path: 'partner-info',
    canActivate: requireTripAccess,
    loadComponent: () => import('./pages/partner-info/partner-info.page').then(m => m.PartnerInfoPage),
  },
  {
    path: 'trip-finances',
    canActivate: [...requireTripAccess, tripFinancesGuard],
    loadComponent: () => import('./pages/trip-finances/trip-finances.page').then(m => m.TripFinancesPage),
  },
  {
    path: 'packing-list',
    canActivate: requireTripAccess,
    loadComponent: () => import('./pages/packing-list/packing-list.page').then(m => m.PackingListPage),
  },
  {
    path: 'resources',
    canActivate: requireTripAccess,
    loadComponent: () => import('./pages/resources/resources.page').then(m => m.ResourcesPage),
  },
  {
    path: 'chatter',
    canActivate: [...requireTripAccess, chatterGuard],
    loadComponent: () => import('./pages/chatter-page/chatter-page.component').then(m => m.ChatterPageComponent),
  },
  {
    path: 'squawks',
    canActivate: requireTripAccess,
    loadComponent: () => import('./pages/squawks-page/squawks-page.component').then(m => m.SquawksPageComponent),
  },
  {
    path: 'faq',
    canActivate: requireTripAccess,
    loadComponent: () => import('./pages/faq/faq.page').then(m => m.FaqPage),
  },
  {
    path: 'trip-event',
    canActivate: [...requireTripAccess, panelContextGuard('edit-trip-event', '/prep-planning')],
    loadComponent: () =>
      import('./pages/trip-event-page/trip-event-page.component').then(
        m => m.TripEventPageComponent,
      ),
  },
  {
    path: 'team-document-upload',
    canActivate: [...requireTripAccess, teamDocumentUploadGuard],
    loadComponent: () =>
      import('./pages/team-document-upload-page/team-document-upload-page.component').then(
        m => m.TeamDocumentUploadPageComponent,
      ),
  },
  {
    path: 'request-travel-quote',
    canActivate: [...requireTripAccess, requestTravelQuoteGuard],
    loadComponent: () =>
      import('./pages/request-travel-quote-page/request-travel-quote-page.component').then(
        m => m.RequestTravelQuotePageComponent,
      ),
  },
  {
    path: 'trip-documents',
    canActivate: [...requireTripAccess, tripDocumentsGuard],
    loadComponent: () =>
      import('./pages/trip-documents/trip-documents.page').then(m => m.TripDocumentsPage),
  },
  {
    path: 'coffee-fundraising',
    canActivate: [...requireTripAccess, coffeeFundraisingGuard],
    loadComponent: () =>
      import('./pages/coffee-fundraising/coffee-fundraising.page').then(
        m => m.CoffeeFundraisingPage,
      ),
  },
  {
    path: 'survey',
    canActivate: [...requireTripAccess, surveyGuard],
    loadComponent: () => import('./pages/survey/survey.page').then(m => m.SurveyPage),
  },
  {
    path: 'payment',
    canActivate: [...requireTripAccess, paymentGuard],
    loadComponent: () => import('./pages/payment-page/payment-page.component').then(m => m.PaymentPageComponent),
  },
  {
    path: 'money-out-payment',
    canActivate: [...requireTripAccess, moneyOutPaymentGuard],
    loadComponent: () =>
      import('./pages/money-out-payment-page/money-out-payment-page.component').then(
        m => m.MoneyOutPaymentPageComponent,
      ),
  },
  {
    path: 'profile',
    canActivate: requireAuth,
    loadComponent: () => import('./pages/profile-page/profile-page.component').then(m => m.ProfilePageComponent),
  },
  {
    path: 'change-password',
    canActivate: requireAuth,
    loadComponent: () =>
      import('./pages/change-password-page/change-password-page.component').then(
        m => m.ChangePasswordPageComponent,
      ),
  },
  {
    path: 'change-email',
    canActivate: requireAuth,
    loadComponent: () =>
      import('./pages/change-email-page/change-email-page.component').then(
        m => m.ChangeEmailPageComponent,
      ),
  },
  {
    path: 'location-swap',
    canActivate: requireTripAccess,
    loadComponent: () =>
      import('./pages/location-swap-page/location-swap-page.component').then(m => m.LocationSwapPageComponent),
  },
  {
    path: 'trip-qr',
    canActivate: requireTripAccess,
    loadComponent: () =>
      import('./pages/trip-qr-page/trip-qr-page.component').then(m => m.TripQrPageComponent),
  },
  {
    path: 'fundraising-faq',
    canActivate: requireTripAccess,
    loadComponent: () =>
      import('./pages/fundraising-faq-page/fundraising-faq-page.component').then(
        m => m.FundraisingFaqPageComponent,
      ),
  },
  {
    path: 'prep-planning',
    canActivate: requireTripAccess,
    loadComponent: () => import('./pages/prep-planning/prep-planning.page').then(m => m.PrepPlanningPage),
  },
  {
    path: 'desktop-required',
    canActivate: requireTripAccess,
    loadComponent: () => import('./desktop-required/desktop-required.page').then(m => m.DesktopRequiredPage),
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
